import { useCallback, useContext, useEffect, useRef } from 'react'

import { DismissableLayerProps } from '@radix-ui/react-dismissable-layer'

import { useAppContext } from 'app/useAppContext'
import { recordApi } from 'data/api/recordApi'
import { useObject } from 'data/hooks/objects'
import useLDFlags from 'data/hooks/useLDFlags'
import { getCsvFilename } from 'features/admin/datagrid/Toolbar/utils'
import { useConfirmDeleteTableModal } from 'features/admin/settings/object/useConfirmDeleteTableModal'
import {
    getIsStackerNativeObject,
    getIsStackerSharedObject,
    getIsStackerSharedObjectCopy,
} from 'features/admin/stackerNativeObjectUtils'
import { useDeleteTableState } from 'features/AppSettings/hooks/useDeleteTableState'
import { DeleteSyncedObjConfirmationModal } from 'features/DataConnections/DataConnectionsManager/DeleteSyncedObjConfirmationModal'
import { DataGridContext } from 'features/workspace/AdminSideTray/manage-data/DataGridContext'
import { ManageDataShareTableModal } from 'features/workspace/AdminSideTray/manage-data/ManageDataShareTableModal'

import { useGlobalModal } from 'ui/components/Modal/useGlobalModal'
import { useToast } from 'ui/components/Toast'

type UseManageDataTabDropdownProps = {
    objectSid: string
    onOpenChange: (open: boolean) => void
    onOpenImportModal: () => void
    onOpenPermissionsModal: () => void
    onOpenSettingsModal: () => void
    onOpenRenamePopup: () => void
}

export function useManageDataTabDropdown({
    objectSid,
    onOpenChange,
    onOpenImportModal,
    onOpenPermissionsModal,
    onOpenSettingsModal,
    onOpenRenamePopup,
}: UseManageDataTabDropdownProps) {
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange

    const { flags } = useLDFlags()
    const toast = useToast()

    const { setIsTrashMode, isTrashMode } = useContext(DataGridContext)

    const isTrashModeRef = useRef(isTrashMode)
    isTrashModeRef.current = isTrashMode

    const trashLabel = isTrashMode ? 'Exit trash' : 'Trash'

    const onGoToTrash = useCallback(() => {
        setIsTrashMode(!isTrashModeRef.current)
    }, [setIsTrashMode])

    const deferAfterClose = useCallback((fn: () => void) => {
        // Wait for the blur events to finish.
        setTimeout(() => {
            fn()
        }, 300)
    }, [])

    const handleOpenImportModal = useCallback(() => {
        deferAfterClose(onOpenImportModal)
    }, [deferAfterClose, onOpenImportModal])

    const allowImport = !!flags.csvImporter

    const { selectedStack } = useAppContext()

    const { object } = useObject(objectSid)
    const objectRef = useRef(object)
    objectRef.current = object

    const onExportData = useCallback(async () => {
        if (!object || !selectedStack) return

        try {
            const filename = getCsvFilename(object, selectedStack)
            await recordApi.downloadCsvRecords({
                object,
                filename,
            })
        } catch (error) {
            console.error(error)
            toast({
                type: 'error',
                title: 'There was a problem exporting the data',
                startIcon: { name: 'AlertCircle' },
                helperText: 'Please try again later. If the issue persists, contact support.',
            })
        }
    }, [object, selectedStack, toast])

    const handleOpenPermissionsModal = useCallback(() => {
        deferAfterClose(onOpenPermissionsModal)
    }, [deferAfterClose, onOpenPermissionsModal])

    const handleOpenSettingsModal = useCallback(() => {
        deferAfterClose(onOpenSettingsModal)
    }, [deferAfterClose, onOpenSettingsModal])

    const { show: showShareSettingsModal, close: closeShareSettingsModal } =
        useGlobalModal(ManageDataShareTableModal)
    const closeShareSettingsModalRef = useRef(closeShareSettingsModal)
    closeShareSettingsModalRef.current = closeShareSettingsModal

    const handleOpenSharingSettingsModal = useCallback(() => {
        deferAfterClose(() => showShareSettingsModal({ objectSid }))
    }, [deferAfterClose, objectSid, showShareSettingsModal])

    const isNativeTable = getIsStackerNativeObject(object)
    const isSharedTable = getIsStackerSharedObject(object)
    const isSharedTableCopy = getIsStackerSharedObjectCopy(object)

    const showShare = flags.sharedTables
    const allowShare = !isSharedTableCopy && !object?.system_object_type
    const shareLabel = isSharedTable ? 'Stop sharing with Workspace' : 'Share with Workspace'
    const shareInfoText = allowShare ? '' : 'This table cannot be shared with the Workspace'

    const {
        title: deleteLabel,
        description: deleteInfoText,
        canDelete,
    } = useDeleteTableState(object)
    const isProtectedByGrant = object?.connection_options?.is_protected_by_grant

    const allowDelete = !isProtectedByGrant && canDelete

    const showNativeDelete = isNativeTable || isSharedTableCopy
    const { showModal: showNativeDeleteModal } = useConfirmDeleteTableModal(object)
    const { show: showDeleteSyncedObjModal } = useGlobalModal(DeleteSyncedObjConfirmationModal)
    const onDelete = useCallback(() => {
        const object = objectRef.current
        if (!object) return

        if (showNativeDelete) {
            showNativeDeleteModal()
        } else {
            showDeleteSyncedObjModal({ object })
        }
    }, [showDeleteSyncedObjModal, showNativeDelete, showNativeDeleteModal])

    const onPointerDownOutside: DismissableLayerProps['onPointerDownOutside'] = useCallback((e) => {
        if (e.detail.originalEvent.defaultPrevented) {
            e.preventDefault()
        }
    }, [])

    const allowRename = isNativeTable
    const renameInfoText = allowRename ? '' : 'This table cannot be renamed'

    const handleOpenRenamePopup = useCallback(() => {
        deferAfterClose(onOpenRenamePopup)
    }, [deferAfterClose, onOpenRenamePopup])

    // Close everything when the objectSid changes.
    useEffect(() => {
        onOpenChangeRef.current(false)
        closeShareSettingsModalRef.current()
    }, [objectSid])

    return {
        onGoToTrash,
        trashLabel,
        handleOpenImportModal,
        allowImport,
        onExportData: onExportData,
        handleOpenPermissionsModal,
        handleOpenSettingsModal,
        handleOpenSharingSettingsModal,
        showShare,
        allowShare,
        shareLabel,
        shareInfoText,
        allowDelete,
        deleteLabel,
        deleteInfoText,
        onDelete,
        onPointerDownOutside,
        handleOpenRenamePopup,
        allowRename,
        renameInfoText,
    }
}
